'use client';

import type { ComponentType } from 'react';
import { useTranslations } from 'next-intl';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  Alert01Icon,
  AlertCircleIcon,
  InformationCircleIcon,
} from '@terra/ui-icons';
import { MonoSmall } from '../../../../components/typography';
import type {
  BrowserConsoleEntry,
  BrowserConsoleLevel,
} from '../utils/browser-tool-output';

type ConsoleLevelStyle = {
  color: string;
  background?: string;
  Icon?: ComponentType<{ size?: number }>;
};

const CONSOLE_LEVEL_STYLE: Record<BrowserConsoleLevel, ConsoleLevelStyle> = {
  log: { color: 'fg.default' },
  debug: { color: 'fg.muted' },
  info: { color: 'fg.info', Icon: InformationCircleIcon },
  warning: {
    color: 'fg.warning',
    background: 'bg.warning',
    Icon: Alert01Icon,
  },
  error: { color: 'fg.error', background: 'bg.error', Icon: AlertCircleIcon },
};

type BrowserConsoleProps = {
  url?: string;
  messages: BrowserConsoleEntry[];
};

function ConsoleRow({
  entry,
  color,
  Icon,
  background,
}: {
  entry: BrowserConsoleEntry;
  color: string;
  Icon?: ComponentType<{ size?: number }>;
  background?: string;
}) {
  return (
    <HStack alignItems="flex-start" gap={2} px={2} py={1} bg={background}>
      <Box
        w="3.5"
        h="3.5"
        mt="0.5"
        flexShrink={0}
        color={color}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {Icon ? <Icon size={14} /> : null}
      </Box>
      <MonoSmall color={color} whiteSpace="pre-wrap" wordBreak="break-word">
        {entry.text}
      </MonoSmall>
    </HStack>
  );
}

export function BrowserConsole({ url, messages }: BrowserConsoleProps) {
  const t = useTranslations('designSystem.chat.browserResult');

  return (
    <VStack
      alignItems="stretch"
      gap={0}
      width="100%"
      borderWidth="1px"
      borderColor="border.subtle"
      borderRadius="md"
      bg="bg.canvas"
      overflow="hidden"
      maxH="360px"
    >
      {url ? (
        <Box
          px={3}
          py={1.5}
          borderBottomWidth="1px"
          borderColor="border.subtle"
          bg="bg.subtle"
        >
          <MonoSmall color="fg.muted">{url}</MonoSmall>
        </Box>
      ) : null}
      <Box overflow="auto">
        {messages.length === 0 ? (
          <ConsoleRow
            entry={{ level: 'log', text: t('emptyConsole') }}
            color="fg.disabled"
          />
        ) : (
          messages.map((entry, index) => {
            const style = CONSOLE_LEVEL_STYLE[entry.level];

            return (
              <ConsoleRow
                key={`${entry.level}-${index}`}
                entry={entry}
                color={style.color}
                Icon={style.Icon}
                background={style.background}
              />
            );
          })
        )}
      </Box>
    </VStack>
  );
}
